/**
 * DivisionService — the signed-in operator's own division (platform mode).
 *
 * The frame's mark shows the division's logo, as Manage shows "RDX" and the
 * employee app brands from the viewer's leaf division
 * (`concierge-desktop/screens/app-frame.md`, Data). `myDivision` resolves the
 * division by the operator's email domain and needs only a signed-in user
 * (`@Auth()`); it is null when the operator belongs to no division, and the
 * shell then falls back to the customer (CompanyService).
 *
 * Same pattern as CompanyService: the auth guard fetches it once, exposed as
 * a signal. Demo mode never fetches.
 */

import { Injectable, inject, signal } from '@angular/core';

import { RDX_VISITORS_ENV } from './env';
import { GqlClient } from './gql-client';

/** The operator's division, as the shell needs it. */
export interface Division {
  name: string | null;
  /** Logo URL (usable directly as an <img> src), or null. */
  logo: string | null;
  /** Logos for light and dark papers, when the division set them. */
  logoLight: string | null;
  logoDark: string | null;
  /** The brand colour (hex), the Division accent's source; null when unset. */
  primaryColor: string | null;
}

/** `myDivision` = the current user's division, resolved by email domain (no args). */
const MY_DIVISION_QUERY = `query MyDivision {
  myDivision {
    name
    logo
    logoLight
    logoDark
    primaryColor
  }
}`;

interface MyDivisionResponse {
  myDivision: {
    name?: string | null;
    logo?: string | null;
    logoLight?: string | null;
    logoDark?: string | null;
    primaryColor?: string | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class DivisionService {
  private readonly gql = inject(GqlClient);

  private readonly _division = signal<Division | null>(null);
  /** The operator's division (null in demo mode, with no division, or before the fetch resolves). */
  readonly division = this._division.asReadonly();

  private loaded = false;
  private inFlight: Promise<void> | null = null;

  /**
   * Fetch the operator's division once. Single-flight + cached (the guard runs
   * on every navigation). No-op in demo mode. Failures are swallowed — the
   * mark falls back to the customer's logo.
   */
  ensureLoaded(): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE || this.loaded) return Promise.resolve();
    this.inFlight ??= this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    try {
      const data = await this.gql.gql<MyDivisionResponse>(MY_DIVISION_QUERY);
      const d = data.myDivision;
      this._division.set(
        d
          ? {
              name: d.name ?? null,
              logo: d.logo ?? null,
              logoLight: d.logoLight ?? null,
              logoDark: d.logoDark ?? null,
              primaryColor: d.primaryColor ?? null,
            }
          : null,
      );
      this.loaded = true;
    } catch {
      // Best-effort — a failed division fetch must not block the app; the
      // mark falls back to the customer's logo and name.
    }
  }
}

/** The logos, name and colour the frame brands with. */
export interface Brand {
  name: string | null;
  logo: string | null;
  logoLight: string | null;
  logoDark: string | null;
  primaryColor: string | null;
}

/**
 * The frame's brand: the operator's division, else the customer
 * (app-frame.md, Data). The logos travel as one set, so a division with only
 * `logo` never borrows the customer's `logoLight`.
 */
export function frameBrand(division: Brand | null, company: Brand | null): Brand {
  const hasLogo = (b: Brand | null) => !!(b?.logo || b?.logoLight || b?.logoDark);
  const logos = hasLogo(division) ? division : hasLogo(company) ? company : null;
  return {
    name: division?.name?.trim() || company?.name?.trim() || null,
    logo: logos?.logo ?? null,
    logoLight: logos?.logoLight ?? null,
    logoDark: logos?.logoDark ?? null,
    primaryColor: division?.primaryColor || company?.primaryColor || null,
  };
}

/** The logo for the paper the mark prints on: the variant for it, else the plain logo. */
export function logoForPaper(brand: Brand, dark: boolean): string | null {
  return (dark ? brand.logoDark : brand.logoLight) ?? brand.logo ?? null;
}
